import { Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  STATUS_COLORS,
  WorkflowRun,
  useEntityWorkflowRuns,
} from '../health/api';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr auto',
    gap: theme.spacing(1),
    alignItems: 'baseline',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13,
  },
  meta: { color: theme.palette.text.secondary, fontSize: 12 },
  time: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
}));

const mark = (r: WorkflowRun) => {
  if (r.status !== 'completed') return { symbol: '●', color: '#5C71B7' };
  if (r.conclusion === 'success')
    return { symbol: '✓', color: STATUS_COLORS.ok };
  if (r.conclusion === 'failure' || r.conclusion === 'timed_out')
    return { symbol: '✕', color: STATUS_COLORS.critical };
  return { symbol: '–', color: '#8A8FA8' };
};

const ago = (iso: string) => {
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (min < 60) return `${min} мин назад`;
  if (min < 48 * 60) return `${Math.round(min / 60)} ч назад`;
  return `${Math.round(min / 1440)} дн назад`;
};

export const WorkflowRunsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { value, error, loading } = useEntityWorkflowRuns(
    stringifyEntityRef(entity),
  );

  let content;
  if (loading) {
    content = <Progress />;
  } else if (!value) {
    content = (
      <Typography color="error">
        Не удалось получить запуски: {error?.message}
      </Typography>
    );
  } else if (!value.runs.length) {
    content = <Typography className={classes.meta}>Запусков нет</Typography>;
  } else {
    content = value.runs.map(r => {
      const m = mark(r);
      return (
        <div key={r.url} className={classes.row}>
          <span style={{ color: m.color }}>{m.symbol}</span>
          <div>
            <Link to={r.url} className={classes.title}>
              {r.title || r.workflow}
            </Link>
            <div className={classes.meta}>
              {[
                r.title && r.title !== r.workflow ? r.workflow : '',
                r.branch,
                r.sha,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <span className={classes.time}>{ago(r.createdAt)}</span>
        </div>
      );
    });
  }

  return (
    <InfoCard
      title="Запуски GitHub Actions"
      deepLink={
        value
          ? {
              title: 'Все запуски',
              link: `https://github.com/${value.repository}/actions`,
            }
          : undefined
      }
    >
      {content}
    </InfoCard>
  );
};
